import { supabase } from "../lib/supabase";
import { notificationQueueService } from "./notificationQueueService";
import { getCurrentPlatform, PLATFORMS } from "../utils/platform";
import { authService } from "./authService";

// Map frontend platform keys to email-config keys (no underscores)
const PLATFORM_TO_EMAIL_KEY = new Map([
  [PLATFORMS.ISOGYAAN, "ISOGYAAN"],
  [PLATFORMS.SKILL_COMPLIANCE, "SKILLCOMPLIANCE"],
  [PLATFORMS.SKILL_COMPLIANCE_CA, "SKILLCOMPLIANCE_CA"],
  [PLATFORMS.PROCERT_COMPLIANCE, "PROCERTCOMPLIANCE"],
]);

function getEmailPlatformKey() {
  return PLATFORM_TO_EMAIL_KEY.get(getCurrentPlatform()) || "ISOGYAAN";
}

export const notificationService = {
  // Create a new in-app notification via edge function (supports cross-user)
  async createNotification(userId, type, title, message, data = {}) {
    try {
      const session = await authService.getValidSession();
      if (!session?.access_token) throw new Error("Authentication required");

      const { data: notification, error } = await supabase.functions.invoke(
        "create-notification",
        {
          body: { user_id: userId, type, title, message, data },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        },
      );

      if (error) throw error;
      return notification;
    } catch (error) {
      console.error("Create notification error:", error);
      throw new Error("Failed to create notification");
    }
  },

  // Get notifications for a user
  async getNotifications(userId, unreadOnly = false, limit = 50) {
    try {
      let query = supabase
        .from("notifications")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(limit);

      if (unreadOnly) {
        query = query.eq("read", false);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Get notifications error:", error);
      return [];
    }
  },

  // Get unread count
  async getUnreadCount(userId) {
    try {
      const { count, error } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userId)
        .eq("read", false);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error("Get unread count error:", error);
      return 0;
    }
  },

  // Mark notification as read
  async markAsRead(notificationId) {
    try {
      const { error } = await supabase
        .from("notifications")
        .update({ read: true })
        .eq("id", notificationId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error("Mark as read error:", error);
      return false;
    }
  },

  // Mark all notifications as read
  async markAllAsRead(userId) {
    try {
      const { error } = await supabase
        .from("notifications")
        .update({ read: true })
        .eq("user_id", userId)
        .eq("read", false);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error("Mark all as read error:", error);
      return false;
    }
  },

  // Subscribe to real-time notifications. `onStatus` receives the channel
  // subscription state (SUBSCRIBED, CHANNEL_ERROR, TIMED_OUT, CLOSED) so the
  // caller can fall back to polling when live delivery is unavailable.
  subscribeToNotifications(userId, callback, onStatus) {
    const channel = supabase
      .channel(`notifications:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          callback(payload.new);
        },
      )
      .subscribe((status, err) => {
        if (err) console.warn("Notification realtime subscription:", err);
        onStatus?.(status);
      });

    return channel;
  },

  // Unsubscribe from notifications
  unsubscribe(channel) {
    if (channel) {
      supabase.removeChannel(channel);
    }
  },

  // ===== EMAIL EDGE FUNCTIONS =====

  /**
   * Send a magic link email for passwordless authentication.
   * Used by login flow.
   */
  async sendMagicLinkEmail(email, redirectTo = null) {
    try {
      const payload = { email };
      if (redirectTo) {
        payload.redirectTo = redirectTo;
      }

      const { data, error } = await supabase.functions.invoke(
        "send-magic-link-email",
        {
          body: payload,
        },
      );

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error("Error sending magic link email:", error);
      throw new Error("Failed to send magic link email");
    }
  },

  /**
   * Send a contact form email.
   * Used by contact page.
   */
  async sendContactEmail(formData, { partnerId } = {}) {
    try {
      const { data, error } = await supabase.functions.invoke(
        "submit-contact-form",
        {
          body: {
            name: formData.name,
            email: formData.email,
            subject: formData.subject,
            message: formData.message,
            platform: getEmailPlatformKey(),
            partnerId: partnerId || null,
          },
        },
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return { success: true };
    } catch (error) {
      console.error("Failed to queue contact email:", error);
      throw error;
    }
  },

  // ===== CALL BOOKING EDGE FUNCTIONS =====

  /**
   * Schedule a call with the team.
   * Saves booking to database and notifies admins.
   */
  async scheduleCall({ name, phone, email, scheduled_date, scheduled_time }) {
    try {
      const { data, error } = await supabase.functions.invoke("schedule-call", {
        body: {
          type: "scheduled_call",
          name,
          phone,
          email,
          scheduled_date,
          scheduled_time,
        },
      });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error("Error scheduling call:", error);
      throw new Error("Failed to schedule call");
    }
  },

  /**
   * Request an immediate callback.
   * Saves request to database and notifies admins for urgent response.
   */
  async requestCallback({ name, phone, email }) {
    try {
      const { data, error } = await supabase.functions.invoke("schedule-call", {
        body: {
          type: "callback_request",
          name,
          phone,
          email,
        },
      });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error("Error requesting callback:", error);
      throw new Error("Failed to request callback");
    }
  },

  /**
   * Submit a support ticket (refund request, general inquiry, etc.)
   * Saves ticket to database and notifies admins.
   */
  async submitSupportTicket({
    ticket_type,
    name,
    email,
    phone,
    course_name,
    order_id,
    reason,
    additional_details,
    partner_id,
    attachments,
  }) {
    try {
      const { data, error } = await supabase.functions.invoke(
        "submit-support-ticket",
        {
          body: {
            ticket_type,
            name,
            email,
            phone,
            course_name,
            order_id,
            reason,
            additional_details,
            partner_id,
            attachments,
          },
        },
      );

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error("Error submitting support ticket:", error);
      throw new Error("Failed to submit support ticket");
    }
  },

  /**
   * Notify admin(s) about a new instructor signup.
   * Used by instructor onboarding page.
   */
  async notifyInstructorSignup({ specializations, partnerName }) {
    // Routed through an edge function (service_role) because unapproved
    // instructors cannot insert into notification_queue under tightened RLS.
    try {
      const { data, error } = await supabase.functions.invoke(
        "submit-instructor-signup",
        {
          body: {
            event: "instructor_signup",
          },
        },
      );
      if (error) throw error;
      return { success: true, queued: data?.queued ?? 0 };
    } catch (error) {
      console.error("Failed to notify instructor signup:", error);
      return { success: false };
    }
  },
};
